import debug from 'debug';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { HTTP_DEFAULT_TIMEOUT_MS, MAX_AUTH_RETRIES } from '../config/auth.config';
import { validateAuthEndpointResponse } from './httpClient.validators';
import {
  handleMaxRetriesExceeded,
  logTerminalRefreshFailure,
  performAuthRetry,
  shouldFastFailOffline,
  shouldRetry401,
  trackRetryAttempt,
} from './httpClient.retryLogic';

// Debug namespaces for HTTP client operations
const debugHttp = debug('impl:step5:httpClient');
const debugAuth = debug('impl:step5:auth');
const debugValidation = debug('impl:step6:httpClient');
const debugError = debug('impl:step5:error');

// HTTP client configuration constants
const HTTP_TIMEOUT_MS = HTTP_DEFAULT_TIMEOUT_MS; // default from config
const RETRY_JITTER_MS = 250; // Random jitter for retry delay (milliseconds)

function createMissingRefreshTokenError(response) {
  const error = new Error('refresh token not found');
  error.isRefreshError = true;
  error.response = response;
  return error;
}

export function createHttpClient({
  baseURL,
  tokenManager,
  authlessPaths = ['/auth/login', '/auth/refresh', '/auth/logout'],
  retry = { retries: 3, baseMs: 1000, capMs: 8000 },
}) {
  debugHttp('step 5: creating HTTP client', {
    baseURL,
    authlessPathsCount: authlessPaths.length,
    retryConfig: retry
  });
  const client = axios.create({ baseURL, timeout: HTTP_TIMEOUT_MS });
  const apiOrigin = safeOrigin(baseURL);

  axiosRetry(client, {
    retries: retry.retries,
    retryDelay: (retryCount, error) => {
      const cap = retry.capMs;
      const ra = error?.response?.headers?.['retry-after'];
      if (ra) {
        const n = Number(ra);
        if (!Number.isNaN(n)) return Math.min(n * 1000, cap);
        const d = Date.parse(ra);
        if (!Number.isNaN(d)) return Math.min(Math.max(0, d - Date.now()), cap);
      }
      const base = Math.min(Math.pow(2, retryCount) * retry.baseMs, cap);
      return base + Math.floor(Math.random() * RETRY_JITTER_MS);
    },
    retryCondition: (error) => {
      const cfg = error?.config || {};
      if (isAuthless(cfg, authlessPaths, apiOrigin)) return false;
      const status = error?.response?.status;
      const method = (cfg.method || 'GET').toUpperCase();
      const idempotent = ['GET', 'HEAD', 'OPTIONS', 'DELETE'].includes(method);
      return (
        axiosRetry.isNetworkError(error) ||
        axiosRetry.isRetryableError(error) ||
        ((status === 429 || status === 503 || (status >= 500 && status < 600)) && idempotent)
      );
    },
  });

  // Abort management per client
  const pending = new Set();
  function track(config) {
    if (config.signal) return;
    const c = new AbortController();
    config.signal = c.signal;
    config.__abortController = c;
    pending.add(c);
  }
  function untrack(config) {
    const c = config?.__abortController;
    if (c) {
      pending.delete(c);
      delete config.__abortController;
      delete config.signal;
    }
  }
  client.abortAll = () => { for (const c of pending) { try { c.abort(); } catch { /* empty */ } } pending.clear(); };

  client.interceptors.request.use(async (config) => {
    debugHttp('step 5: intercepting request', { 
      method: config.method,
      url: config.url,
      hasAuth: !!config.headers?.Authorization 
    });

    track(config);
    
    const isAuthlessPath = isAuthless(config, authlessPaths, apiOrigin);
    const isSameOrigin = isSameApi(config, apiOrigin);
    
    debugAuth('step 5: checking request auth requirements', { 
      isAuthless: isAuthlessPath,
      isSameOrigin,
      url: config.url 
    });

    if (isAuthlessPath) {
      debugAuth('step 5: authless path, skipping auth header');
      return config;
    }
    
    if (!isSameOrigin) {
      debugAuth('step 5: different origin, skipping auth header');
      return config;
    }

    config.headers = config.headers || {};
    config.meta = config.meta || {};

    const auth = tokenManager.getAuthData?.() || null;

    // Proactive refresh with skew
    if (!config.meta.didRefresh && tokenManager.shouldProactivelyRefresh(auth)) {
      debugAuth('step 5: proactive refresh needed before request', { 
        url: config.url,
        hasAuth: !!auth 
      });

      try {
        const t = await tokenManager.refreshSingleFlight();
        config.headers.Authorization = `Bearer ${t.access_token}`;
        debugAuth('step 5: proactive refresh successful, token attached');
      } catch (e) {
        // ✅ STEP 8: Re-throw offline errors immediately
        if (shouldFastFailOffline(e, { debugError })) {
          debugError('step 8: offline error during proactive refresh');
          throw e;
        }
        if (tokenManager.isTerminalRefreshError(e)) {
          logTerminalRefreshFailure(e, {
            config,
            flow: 'proactive_request',
          });
          debugError('step 5: terminal refresh error, logging out', { 
            errorCode: e.code,
            errorMessage: e.message 
          });
          tokenManager.logout();
          client.abortAll();
        } else {
          debugAuth('step 5: non-terminal refresh error, continuing with existing token');
        }
        // Non-terminal errors: continue with existing token
      }
    } else if (auth?.token && (!config.headers.Authorization || config.meta.authRetryCount)) {
      debugAuth('step 5: attaching token to request', {
        reason: config.meta.authRetryCount ? 'retry' : 'missing header',
      });
      config.headers.Authorization = `Bearer ${auth.token}`;
    }

    debugHttp('step 5: request ready', { 
      hasAuthHeader: !!config.headers.Authorization,
      url: config.url 
    });
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      try { untrack(response.config); } catch { /* empty */ }

      debugHttp('step 5: response received', { 
        status: response.status,
        url: response.config?.url 
      });

      // ✅ STEP 6: Validate tokens from auth endpoints before they're used
      validateAuthEndpointResponse(response, { debugValidation, debugError });

      debugHttp('step 5: response validation complete, returning');
      return response;
    },
    async (error) => {
      try { untrack(error.config); } catch { /* empty */ }

      debugError('step 5: response error intercepted', { 
        status: error?.response?.status,
        code: error.code,
        url: error?.config?.url 
      });

      // ✅ STEP 8: Fast-fail for offline errors
      if (shouldFastFailOffline(error, { debugError })) {
        return Promise.reject(error);
      }

      const cfg = error?.config || {};

      // Password expired special case left to app layer, not here.

      if (!shouldRetry401(error, cfg, { authlessPaths, apiOrigin, isAuthless, isSameApi })) {
        return Promise.reject(error);
      }

      const auth = tokenManager.getAuthData?.() || null;
      if (auth?.token && tokenManager.canRefresh(auth) === false) {
        const authError = createMissingRefreshTokenError(error?.response);

        logTerminalRefreshFailure(authError, {
          config: cfg,
          flow: 'reactive_401_retry',
        });
        debugError('step 5: 401 received but session has no refresh capability, logging out', {
          url: cfg.url,
        });
        tokenManager.logout();
        client.abortAll();
        return Promise.reject(authError);
      }

      const retryCount = trackRetryAttempt(cfg, MAX_AUTH_RETRIES, { debugAuth });

      if (retryCount > MAX_AUTH_RETRIES) {
        handleMaxRetriesExceeded({
          client,
          config: cfg,
          error,
          maxRetries: MAX_AUTH_RETRIES,
          tokenManager,
          debugAuth,
          debugError,
        });
        return Promise.reject(error);
      }

      return performAuthRetry({
        client,
        config: cfg,
        maxRetries: MAX_AUTH_RETRIES,
        tokenManager,
        debugAuth,
        debugError,
      });
    }
  );

  return client;
}

function safeOrigin(u) {
  try { return new URL(u).origin; } catch { return ''; }
}
function isSameApi(config, apiOrigin) {
  try { return new URL(config.url, config.baseURL || '').origin === apiOrigin; } catch { return true; }
}
function isAuthless(config, authlessPaths, apiOrigin) {
  try {
    // Per-request opt-out via meta flag
    if (config?.meta?.authless === true) return true;

    const url = new URL(config.url, config.baseURL || '');
    if (url.origin !== apiOrigin) return true; // treat cross-origin as authless
    const path = url.pathname.replace(/\/+$/, '');
    return authlessPaths.includes(path);
  } catch {
    return false;
  }
}
